import { useState, useEffect } from "react";
import { ChevronDown, Truck } from "lucide-react";
import { getBushCabinBuilds, getBushCabinBuildsSummary } from "@/admin/adminApi";

const SUMMARY_RANGES = [
  { key: "24h", label: "24H" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "all", label: "All Time" },
];

const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString("en-NZ")}`;
const timeAgo = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};
// Rough proxy for how long they actually spent on the tool (first save -> last
// save) — NOT a precise "time on page" metric, since a save only fires when
// the build state changes or the bottom sentinel first fires. Sessions well
// over ~10 minutes are almost always just a browser tab left open in the
// background rather than genuine active time, so those get flagged rather
// than left looking like an implausibly long real session.
const timeOnTool = (createdIso, updatedIso) => {
  const secs = Math.round((new Date(updatedIso).getTime() - new Date(createdIso).getTime()) / 1000);
  if (secs < 60) return `${secs}s on tool`;
  const mins = Math.round(secs / 60);
  if (mins < 10) return `${mins}m on tool`;
  return `${mins}m on tool (likely tab left open)`;
};

const EmptyState = ({ label }) => <div className="text-center py-12 text-white/40 text-sm font-mono">{label}</div>;

export default function AdminBushCabinBuildsPage() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryRange, setSummaryRange] = useState("24h");

  useEffect(() => { getBushCabinBuilds().then(setData); }, []);
  useEffect(() => {
    setSummary(null);
    getBushCabinBuildsSummary(summaryRange).then(setSummary);
  }, [summaryRange]);

  return (
    <div data-testid="admin-bushcabin-builds-page">
      <h1 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
        <Truck size={20} className="text-[var(--brand-primary)]" /> Bush Cabin Builds
      </h1>
      <p className="text-white/50 text-sm font-mono mb-6">
        Real builds people have put together on the "Build Your Bush Cabin" tool, whether or not
        they submitted an enquiry — only shows sessions that scrolled far enough to see every
        accessory and awning option, so this is genuine engagement, not a quick bounce.
      </p>

      {/* CABIN TYPE SUMMARY — how many chose each base vehicle in a given timeframe */}
      <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-5 mb-8" data-testid="bushcabin-builds-summary">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <p className="text-white/50 text-xs font-mono uppercase tracking-widest">Builds Started By Cabin Type</p>
          <div className="flex items-center gap-1 overflow-x-auto">
            {SUMMARY_RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`bushcabin-summary-range-${r.key}`}
                onClick={() => setSummaryRange(r.key)}
                className={`shrink-0 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
                  summaryRange === r.key ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-transparent text-white/50 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {!summary ? (
          <p className="text-white/40 text-sm font-mono">Loading…</p>
        ) : summary.total === 0 ? (
          <p className="text-white/40 text-sm font-mono">No engaged builds started in this timeframe.</p>
        ) : (
          <div className="space-y-2" data-testid="bushcabin-summary-breakdown">
            {summary.breakdown.map((row, i) => {
              const pct = summary.total ? Math.round((row.count / summary.total) * 100) : 0;
              return (
                <div key={row.cabinSlug || i} data-testid={`bushcabin-summary-row-${i}`} className="flex items-center gap-3">
                  <span className="text-white text-sm flex-1">{row.cabinName}</span>
                  <div className="flex-1 max-w-[200px] h-2 bg-white/10 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-[var(--brand-primary)]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[var(--brand-primary)] font-mono text-sm w-20 text-right">{row.count} ({pct}%)</span>
                </div>
              );
            })}
            <p className="text-white/40 text-xs font-mono mt-3">{summary.total} total engaged build{summary.total === 1 ? "" : "s"} started in this timeframe.</p>
          </div>
        )}
      </div>

      {!data ? (
        <EmptyState label="Loading…" />
      ) : data.builds.length === 0 ? (
        <EmptyState label="No engaged builds yet — check back once people have used the tool." />
      ) : (
        <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
          {data.builds.map((b, i) => {
            const isOpen = openId === b.id;
            return (
              <div key={b.id}>
                <button
                  type="button"
                  data-testid={`bushcabin-build-row-${i}`}
                  onClick={() => setOpenId(isOpen ? null : b.id)}
                  className="w-full flex items-center justify-between p-4 gap-4 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="flex-1">
                    <div className="text-white text-sm">{b.cabin_name || "—"}</div>
                    <div className="text-white/40 text-xs font-mono mt-1">
                      {(b.summary || []).length} accessor{(b.summary || []).length === 1 ? "y" : "ies"} chosen · {timeOnTool(b.created_at, b.updated_at)} · {timeAgo(b.updated_at)}
                    </div>
                  </div>
                  <div className="text-[var(--brand-primary)] font-mono text-sm shrink-0">{fmtMoney(b.total_price)}</div>
                  <ChevronDown size={16} className={`text-white/40 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] p-4" data-testid={`bushcabin-build-detail-${i}`}>
                    {(b.summary || []).length === 0 ? (
                      <p className="text-white/40 text-sm font-mono">No accessories or awnings added — base camper only.</p>
                    ) : (
                      <ul className="space-y-2 text-sm text-white/80">
                        {b.summary.map((line, j) => (
                          <li key={j} data-testid={`bushcabin-build-detail-${i}-item-${j}`}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
